import type { RoutedTab } from './routedTabs';

/**
 * ## Every tab of every tabbed screen, and the URL it answers on
 *
 * One file, because this is a table somebody has to be able to check against
 * what they expected — and because a tab id is a *link*: it appears in a
 * bookmark, a message, a support ticket. Renaming one breaks something outside
 * this repo, which is a decision worth making in a diff rather than in passing
 * while editing a screen.
 *
 * It is also the reason these do not live beside their screens. A `const` array
 * exported from a file that also exports a component costs fast refresh on that
 * screen, and `Catalog.tsx` is a file people edit; here, nothing re-renders and
 * `tabRoutes.test.tsx` can read the whole table without importing six screens.
 *
 * Ids are what goes in the URL, so they are lower case and hyphenated even
 * where the label key that names them is not — `low-stock` reads
 * `operations.inventory.tabs.lowStock`, because the key is published and the
 * URL is new.
 *
 * `perms` is `canAny`, matching `PermissionRoute`. A tab with none is one the
 * screen's own route gate already covers; `routedTabs.tsx` refuses the rest.
 */

export type CatalogTab = 'items' | 'menus' | 'modifiers' | 'meals' | 'packages' | 'stations';

/** `/catalog/<id>`. "Items" alone no longer says categories live there too. */
export const CATALOG_TABS: readonly RoutedTab<CatalogTab>[] = [
  { id: 'items', labelKey: 'catalog.tabs.itemsAndCategories' },
  { id: 'menus', labelKey: 'catalog.tabs.menus' },
  { id: 'modifiers', labelKey: 'catalog.tabs.modifiers' },
  { id: 'meals', labelKey: 'catalog.tabs.meals' },
  { id: 'packages', labelKey: 'catalog.tabs.packages' },
  { id: 'stations', labelKey: 'catalog.tabs.stations' },
];

export type CrmTab = 'customers' | 'segments' | 'feedback' | 'coupons' | 'promotions';

/**
 * `/customers/<id>`.
 *
 * `segments` is gated on `segments.view` — its own permission family
 * (ADR-0010 §8), separate from `customers.view`: a role that can read customer
 * profiles was never automatically meant to read or build marketing audiences.
 */
export const CRM_TABS: readonly RoutedTab<CrmTab>[] = [
  { id: 'customers', labelKey: 'crm.tabs.customers' },
  { id: 'segments', labelKey: 'crm.tabs.segments', perms: ['segments.view'] },
  { id: 'feedback', labelKey: 'crm.tabs.feedback' },
  { id: 'coupons', labelKey: 'crm.tabs.coupons' },
  { id: 'promotions', labelKey: 'crm.tabs.promotions' },
];

export type InventoryTab =
  | 'items'
  | 'movements'
  | 'low-stock'
  | 'valuation'
  | 'variance'
  | 'margin'
  | 'units'
  | 'warehouses';

/**
 * `/inventory/<id>`.
 *
 * `InventoryReportsController` gates margin and variance on `reports.view`, not
 * on the `inventory.view` that carries the rest of the board — so an operator
 * who may read stock but not reports must not be offered either tab, and typing
 * the URL gets the same refusal the API would give.
 */
export const INVENTORY_TABS: readonly RoutedTab<InventoryTab>[] = [
  { id: 'items', labelKey: 'operations.inventory.tabs.items' },
  { id: 'movements', labelKey: 'operations.inventory.tabs.movements' },
  { id: 'low-stock', labelKey: 'operations.inventory.tabs.lowStock' },
  { id: 'valuation', labelKey: 'operations.inventory.tabs.valuation' },
  { id: 'variance', labelKey: 'operations.inventory.tabs.variance', perms: ['reports.view'] },
  { id: 'margin', labelKey: 'operations.inventory.tabs.margin', perms: ['reports.view'] },
  { id: 'units', labelKey: 'operations.inventory.tabs.units' },
  { id: 'warehouses', labelKey: 'operations.inventory.tabs.warehouses' },
];

export type OrderTab = 'dine-in' | 'takeaway';

/** `/orders/<id>`. */
export const ORDER_TABS: readonly RoutedTab<OrderTab>[] = [
  { id: 'dine-in', labelKey: 'operations.orders.tabs.dineIn' },
  { id: 'takeaway', labelKey: 'operations.orders.tabs.takeaway' },
];

export type ProcurementTab = 'orders' | 'receipts' | 'suppliers' | 'prices';

/** `/procurement/<id>`. */
export const PROCUREMENT_TABS: readonly RoutedTab<ProcurementTab>[] = [
  { id: 'orders', labelKey: 'platform.procurement.tab.orders' },
  { id: 'receipts', labelKey: 'platform.procurement.tab.receipts' },
  { id: 'suppliers', labelKey: 'platform.procurement.tab.suppliers' },
  { id: 'prices', labelKey: 'platform.procurement.tab.prices' },
];

export type DeliveryAggregatorsTab = 'incoming' | 'mappings' | 'unreconciled';

/**
 * `/delivery-aggregators/<id>` — ADR-0028 Part B. `incoming` first and not
 * alphabetically: it is the gate (§B2/§B6), the tab a floor role actually
 * opens the screen for, every other tab is troubleshooting or configuration
 * around it.
 */
/**
 * All three tabs need only the route's own `delivery_aggregators.view` — none
 * declare `perms` here. `mappings` is read-only to a SHIFT_MANAGER (who holds
 * `view`/`accept_reject`/`cancel` but not `manage_mappings`, per
 * `roles.ts`'s own comment: repointing a mapping is a menu-configuration
 * decision, not a floor one) rather than hidden from them — the tab that
 * explains *why* an order was refused at ingestion belongs in front of the
 * person answering that order, even when they cannot fix it themselves.
 * `MappingsTab.tsx` gates its Add/Edit/toggle controls on `manage_mappings`
 * individually, the house "gate the action, not only the route" shape.
 */
export const DELIVERY_AGGREGATORS_TABS: readonly RoutedTab<DeliveryAggregatorsTab>[] = [
  { id: 'incoming', labelKey: 'deliveryAggregators.tabs.incoming' },
  { id: 'mappings', labelKey: 'deliveryAggregators.tabs.mappings' },
  { id: 'unreconciled', labelKey: 'deliveryAggregators.tabs.unreconciled' },
];

export type FleetTab = 'spool' | 'devices' | 'printers' | 'routing' | 'versions';

/**
 * `/fleet/<id>` — §11, the device and printer estate.
 *
 * **Spool first, and not alphabetically.** This is the screen an operator opens
 * at 8pm on a Friday when tickets stop coming out of the kitchen; the
 * configuration tabs beside it are opened once, during install. A landing tab
 * is a statement about what the screen is for, and the answer here is "what is
 * broken right now", not "what hardware exists".
 *
 * The keys mirror what the API enforces (ADR-0009 / `permissions.ts`), so no
 * tab here leads to a 403: the spool is `print_jobs.*`, the registry and the
 * version floor are `devices.*`, and printers, destinations and routing rules
 * are all `printers.*`. Version policy is deliberately gated on `devices.view`
 * rather than on `devices.manage_versions` — a shift manager may read the floor
 * without being able to raise it, and the write button asks for the write key
 * on its own.
 */
export const FLEET_TABS: readonly RoutedTab<FleetTab>[] = [
  { id: 'spool', labelKey: 'fleet.tabs.spool', perms: ['print_jobs.view'] },
  { id: 'devices', labelKey: 'fleet.tabs.devices', perms: ['devices.view'] },
  { id: 'printers', labelKey: 'fleet.tabs.printers', perms: ['printers.view', 'print_bridges.view'] },
  { id: 'routing', labelKey: 'fleet.tabs.routing', perms: ['printers.view'] },
  { id: 'versions', labelKey: 'fleet.tabs.versions', perms: ['devices.view'] },
];

export type SettingsTab =
  | 'spaces'
  | 'pricing'
  | 'staff'
  | 'roles'
  | 'assignments'
  | 'branches'
  | 'devices'
  | 'wifi'
  | 'guest'
  | 'booking'
  | 'plan'
  | 'marketplace';

/** `/settings/<id>`. */
export const SETTINGS_TABS: readonly RoutedTab<SettingsTab>[] = [
  // First tab: a venue cannot trade until its floors, areas and tables exist, and
  // every other tab quietly assumes they already do. A new owner holds none of
  // those keys yet, which is why the first tab is not the same for everyone.
  { id: 'spaces', labelKey: 'settings.tabs.spaces', perms: ['floor_map.view', 'tables.view'] },
  // Immediately after Spaces because those two are what a resource needs before
  // it can trade: somewhere to stand, and a price. Gated on `resources.view`,
  // which is the key `@Controller('pricing-lists')` actually requires — and NOT
  // on `settings.view`, which a venue's floor manager does not hold and which
  // would have hidden the tab from the one person who edits rate cards.
  { id: 'pricing', labelKey: 'settings.tabs.pricing', perms: ['resources.view'] },
  { id: 'staff', labelKey: 'settings.tabs.staff', perms: ['users.view'] },
  { id: 'roles', labelKey: 'settings.tabs.roles', perms: ['roles.view'] },
  { id: 'assignments', labelKey: 'settings.tabs.assignments', perms: ['users.view'] },
  { id: 'branches', labelKey: 'settings.tabs.branches', perms: ['settings.view'] },
  { id: 'devices', labelKey: 'settings.tabs.devices', perms: ['devices.view'] },
  { id: 'wifi', labelKey: 'settings.tabs.wifi', perms: ['settings.view'] },
  // Next to Wi-Fi rather than anywhere else: both are things a venue points at
  // the phone in a guest's hand, and both are read and written on
  // `settings.view` / `settings.update`.
  { id: 'guest', labelKey: 'settings.tabs.guest', perms: ['settings.view'] },
  // §13 / ADR-0018 — the branch's public booking page and its embed token.
  // Beside Guest settings rather than under Integrations or Reservations, on
  // its own `booking_widget.*` family: this is "how a branch is configured
  // for the public", the same question Wi-Fi and Guest settings answer, not
  // "which third party's server may reach this venue's data" (Integrations'
  // `api_clients`/`webhooks`) and not "today's calendar of bookings"
  // (Reservations). `view` alone opens the tab; `configure` and
  // `rotate_key` gate the controls inside it on their own keys, matching
  // `BookingWidgetsController`.
  { id: 'booking', labelKey: 'settings.tabs.booking', perms: ['booking_widget.view'] },
  { id: 'plan', labelKey: 'settings.tabs.plan', perms: ['settings.view'] },
  // ADR-0027 — browsing and installing someone ELSE's app. The developer
  // portal (registering an app, submitting versions, sandbox provisioning)
  // moved out to its own application — a venue operator installs apps, they
  // do not author them, so this venue dashboard's Settings keeps only the
  // installing tenant's half. `canAny` over `marketplace.browse` (the
  // catalogue read) and `marketplace.install`
  // (`MarketplaceInstallationsController.findAll`'s OWN gate, on the "what has
  // this tenant installed" list — not just the write) so a holder of only one
  // still lands on the tab and sees the half they can read, the same I-07
  // shape every other Settings tab in this table already documents. The panel
  // gates `.configure`/`.uninstall`/`.consent_upgrade` again at each control.
  { id: 'marketplace', labelKey: 'settings.tabs.marketplace', perms: ['marketplace.browse', 'marketplace.install'] },
];

export type ReportsTab = 'dashboard' | 'builder' | 'schedules' | 'runs' | 'forecasting';

/**
 * `/reports/<id>` — ADR-0013, the report builder epic landing beside the
 * fixed dashboard it shares a compiler with rather than as a nav row of its
 * own. `dashboard` is the pre-existing fixed panels and ad-hoc query picker;
 * `builder`, `schedules` and `runs` are the saved-report builder, delivery
 * schedules and run history the epic adds.
 *
 * `schedules` carries its own `perms`: `ReportingController` gates every
 * `/reporting/schedules*` route — including the list — on `reports.schedule`,
 * a narrower key than the `reports.view` the route itself requires, so a user
 * who may read reports but was never granted scheduling must not be shown a
 * tab that 403s the moment it loads. `builder` and `runs` need no entry of
 * their own: previewing, saving and listing runs all sit on `reports.view`,
 * which the route already covers, and the write actions inside each tab (save,
 * export, create/edit a schedule) are gated at the control instead.
 */
export const REPORTS_TABS: readonly RoutedTab<ReportsTab>[] = [
  { id: 'dashboard', labelKey: 'reporting.tabs.dashboard' },
  { id: 'builder', labelKey: 'reporting.tabs.builder' },
  { id: 'schedules', labelKey: 'reporting.tabs.schedules', perms: ['reports.schedule'] },
  { id: 'runs', labelKey: 'reporting.tabs.runs' },
  // ADR-0031 — its own key, not `reports.view`: `INVENTORY_MANAGER` and
  // `PURCHASING` hold `forecasting.view` for the replenishment suggestion
  // this feeds, and it happens they also hold `reports.view` today, but the
  // tab's own gate should not depend on that coincidence.
  { id: 'forecasting', labelKey: 'reporting.tabs.forecasting', perms: ['forecasting.view'] },
];

export type MenuGovernanceTab = 'versions' | 'history';

/**
 * `/menu-governance/<id>`. Both tabs are gated on `menu_governance.view` —
 * the same key the route itself requires — so neither carries its own
 * `perms`: a tab with none is one the screen's own route gate already covers.
 *
 * `history` is `asOf` branch history rather than a version list: "what did
 * this branch carry at 13:20 on Friday" is a different question from "show me
 * the versions", answered by a different endpoint
 * (`GET /menu-governance/branches/:branchId/assortment?asOf=`), and deserves
 * its own place in the URL rather than living as a dialog off the version
 * list.
 */
export const MENU_GOVERNANCE_TABS: readonly RoutedTab<MenuGovernanceTab>[] = [
  { id: 'versions', labelKey: 'menuGovernance.tabs.versions' },
  { id: 'history', labelKey: 'menuGovernance.tabs.history' },
];

export type IntegrationsTab = 'providers' | 'installations' | 'webhooks' | 'api-clients';

/**
 * `/integrations/<id>` — §2 the integration substrate (ADR-0002) and §3
 * outbound webhooks (ADR-0004), one screen because ADR-0004 names its own
 * dead-letter and health UI as belonging to "the Integration Health Center
 * (blueprint §2)" rather than to a screen of its own.
 *
 * Gated per tab on the read key each API actually enforces: **providers**
 * and **installations** are `integrations.view`, **webhooks** is
 * `webhooks.view` — two different permission families a tenant can hold
 * independently, so an integration engineer who holds only one still lands on
 * the tab they can read instead of a screen with nothing on it.
 *
 * **api-clients** (ADR-0003) joins them on `api_clients.view` for the same
 * reason: it is the mirror image of Providers/Installations — credentials
 * THIS venue hands out rather than holds — and the same integration engineer
 * is the one who looks at it when a partner's server cannot reach `/public/v1`.
 * **api-clients** joins them rather than living under Settings, on
 * `api_clients.view` (ADR-0003 / ADR-0027): a webhook endpoint is this venue
 * SENDING its events outward on a URL it configures; an `ApiClient` is a
 * partner's server READING or writing this venue's data through
 * `/public/v1` on a credential it configures. Opposite directions of the
 * identical relationship — "a third party's server talks to Tarabeeza on
 * this venue's behalf" — and the tab reuses the same "secret shown once"
 * dialog, the same rotate/revoke split and the same audit reasoning
 * `WebhooksPanel` already established, rather than inventing a second
 * pattern for it under Settings next to Wi-Fi passwords and branch names,
 * which answer a different question ("how is this venue itself
 * configured") than "who outside this venue can reach its API".
 */
export const INTEGRATIONS_TABS: readonly RoutedTab<IntegrationsTab>[] = [
  { id: 'providers', labelKey: 'integrations.tabs.providers', perms: ['integrations.view'] },
  { id: 'installations', labelKey: 'integrations.tabs.installations', perms: ['integrations.view'] },
  { id: 'webhooks', labelKey: 'integrations.tabs.webhooks', perms: ['webhooks.view'] },
  { id: 'api-clients', labelKey: 'integrations.tabs.apiClients', perms: ['api_clients.view'] },
];

export type FiscalTab = 'reconciliation' | 'configuration';

/**
 * `/fiscal/<id>` — §5, ADR-0005. Reconciliation first and not alphabetically:
 * `docs/fiscal.md` names it as the product — pending, accepted, rejected,
 * retrying, with a rejected document an operator task rather than a failed
 * sale — and configuration is what somebody opens once, at setup, to make the
 * first tab meaningful. Both tabs sit on `fiscal.view`, the route's own gate,
 * so neither carries `perms` of its own; every mutating control inside gates
 * itself again on `fiscal.configure` / `.credentials` / `.retry` / `.cancel` /
 * `.resolve`, matching what `FiscalController` and `FiscalDocumentsController`
 * actually enforce per route.
 */
export const FISCAL_TABS: readonly RoutedTab<FiscalTab>[] = [
  { id: 'reconciliation', labelKey: 'fiscal.tabs.reconciliation' },
  { id: 'configuration', labelKey: 'fiscal.tabs.configuration' },
];

export type CentralKitchenTab = 'transfers' | 'production' | 'requisitions' | 'replenishment';

/**
 * `/central-kitchen/<id>` — §18, ADR-0015. Transfers first: it is the tab
 * that answers "where is the van right now", which is the correctness idea
 * this epic exists to surface (stock in transit belongs to nobody and must be
 * accounted for) — opened far more often than the other three, which are
 * planning screens a commissary manager visits on their own schedule. Each
 * tab is gated on its own module's read key, matching the route: a commissary
 * clerk holding only `stock_transfers.view` lands on Transfers and never sees
 * a tab that would 403.
 */
export const CENTRAL_KITCHEN_TABS: readonly RoutedTab<CentralKitchenTab>[] = [
  { id: 'transfers', labelKey: 'centralKitchen.tabs.transfers', perms: ['stock_transfers.view'] },
  { id: 'production', labelKey: 'centralKitchen.tabs.production', perms: ['production.view'] },
  { id: 'requisitions', labelKey: 'centralKitchen.tabs.requisitions', perms: ['requisitions.view'] },
  { id: 'replenishment', labelKey: 'centralKitchen.tabs.replenishment', perms: ['replenishment.view'] },
];

export type ReservationsTab = 'calendar' | 'host-queue';

/**
 * `/reservations/<id>` — §13 / ADR-0018 adds the host queue beside the
 * existing calendar rather than as a nav row of its own: the task note that
 * shipped this epic is explicit that Reservations already has a row, and the
 * queue is a second VIEW of overlapping data (bookings due soon, one system;
 * walk-ins, another), not a separate feature. `host-queue` carries its own
 * `perms`: `HostQueueService.queue` needs `waitlist.view`, a narrower key than
 * `reservations.view`, which the route itself already requires — so a waiter
 * holding only the calendar's key must not be shown a tab that leads nowhere.
 * `calendar` needs none of its own: the route's `reservations.view` already
 * covers it.
 */
export const RESERVATION_TABS: readonly RoutedTab<ReservationsTab>[] = [
  { id: 'calendar', labelKey: 'operations.reservations.tabs.calendar' },
  { id: 'host-queue', labelKey: 'operations.reservations.tabs.hostQueue', perms: ['waitlist.view'] },
];

export type FloorMapMode = 'live' | 'edit' | 'qr';

/**
 * `/floor-map/<id>`.
 *
 * Not a tab strip on screen — it is a segmented control, and `qr` is a whole
 * sheet rather than a panel — but it is the same question: three views of one
 * screen, permission-filtered, that a person should be able to link to and come
 * back to. It replaces a `?mode=edit` query parameter that the screen had to
 * honour exactly once through a ref, because switching back to Live would
 * otherwise bounce straight into the editor again.
 */
export const FLOOR_MAP_MODES: readonly RoutedTab<FloorMapMode>[] = [
  { id: 'live', labelKey: 'spaces.mode.live' },
  { id: 'edit', labelKey: 'spaces.mode.edit', perms: ['floor_map.update'] },
  { id: 'qr', labelKey: 'spaces.mode.qr', perms: ['tables.view'] },
];

export type RiskTab = 'queue' | 'health' | 'rules' | 'reviewers';

/**
 * `/risk/<id>`. ADR-0017 §"Permissions" — `risk.review` is what actually
 * exposes a subject's identity, so **queue** is gated on it alone. **health**
 * accepts either `risk.review` or `risk.configure`, mirroring
 * `RiskHealthController`'s manual OR check (there is no OR form for
 * `@RequirePermissions`, and `RoutedTab.perms` is `canAny` here too, so
 * listing both keys reproduces the same rule). **rules** and **reviewers**
 * are `risk.configure` only — a threshold and a notify-roster carry no
 * allegation of their own.
 */
export const RISK_TABS: readonly RoutedTab<RiskTab>[] = [
  { id: 'queue', labelKey: 'risk.tabs.queue', perms: ['risk.review'] },
  { id: 'health', labelKey: 'risk.tabs.health', perms: ['risk.review', 'risk.configure'] },
  { id: 'rules', labelKey: 'risk.tabs.rules', perms: ['risk.configure'] },
  { id: 'reviewers', labelKey: 'risk.tabs.reviewers', perms: ['risk.configure'] },
];

export type WorkforceTab =
  | 'employees'
  | 'timesheets'
  | 'exceptions'
  | 'policy'
  | 'roster'
  | 'templates'
  | 'availability'
  | 'swaps'
  | 'labour'
  | 'mine';

/**
 * `/workforce/<id>`. ADR-0008 — `employees` is its own permission family;
 * `timesheets`, `exceptions` and `policy` are `attendance.*` (`policy`'s own
 * write is `settings.update`, but *reading* the effective rules is
 * `attendance.view` like the other two, matching `AttendanceController`).
 * Compensation is not a tab: it is reached from inside one employee's own
 * profile, behind `compensation.view`, never from a list of everyone's pay.
 *
 * `roster`, `templates`, `availability` and `swaps` are `scheduling.view` —
 * the read side of ADR-0008 §5, which every one of them needs before any
 * mutating control inside the tab is even offered (`RosterTab` etc. gate
 * those individually on `scheduling.manage`/`.publish`/`.swap_approve`).
 * `labour` lists only `scheduling.view` here too: its route actually needs
 * `scheduling.view` AND `reports.view` together (an AND `RequirePermissions`
 * pair, not an OR — `LabourTab` checks both itself before it ever calls the
 * endpoint, since this array's `perms` is `canAny` and cannot express AND).
 *
 * `mine` is `scheduling.view_own` — a different key from every tab above it,
 * held by every floor role and not implied by `scheduling.view` (reading the
 * WHOLE branch's roster) or `scheduling.manage`. Listed LAST: the array
 * order is also the landing-tab order (`useRoutedTabs` opens the first tab
 * an actor may see), and a WAITER or CASHIER holding only `view_own` sees
 * exactly one tab regardless of where it sits — but a manager who already
 * lands on `employees` must keep doing so, which position-last guarantees.
 */
export const WORKFORCE_TABS: readonly RoutedTab<WorkforceTab>[] = [
  { id: 'employees', labelKey: 'workforce.tabs.employees', perms: ['employees.view'] },
  { id: 'timesheets', labelKey: 'workforce.tabs.timesheets', perms: ['attendance.view'] },
  { id: 'exceptions', labelKey: 'workforce.tabs.exceptions', perms: ['attendance.view'] },
  { id: 'policy', labelKey: 'workforce.tabs.policy', perms: ['attendance.view'] },
  { id: 'roster', labelKey: 'workforce.tabs.roster', perms: ['scheduling.view'] },
  { id: 'templates', labelKey: 'workforce.tabs.templates', perms: ['scheduling.view'] },
  { id: 'availability', labelKey: 'workforce.tabs.availability', perms: ['scheduling.view'] },
  { id: 'swaps', labelKey: 'workforce.tabs.swaps', perms: ['scheduling.view'] },
  { id: 'labour', labelKey: 'workforce.tabs.labour', perms: ['scheduling.view'] },
  { id: 'mine', labelKey: 'workforce.tabs.mine', perms: ['scheduling.view_own'] },
];

export type StorefrontTab = 'branding' | 'domains' | 'branches' | 'carts';

/**
 * `/storefront/<id>`. ADR-0016 §8 — a new top-level screen, not a Settings
 * tab: branding, domains, branch publishing and abandoned-cart telemetry are
 * one operator's whole job on a chain with an online storefront, the same
 * weight as Fleet or Menu Governance getting their own rail row.
 *
 * `domains` is its own permission (`storefront.manage_domains`), separate
 * from `branding`/`branches`' `storefront.view`+`.update`: attaching a
 * hostname decides which tenant an unauthenticated request reads, which is a
 * different blast radius from editing the site's copy (see the controller's
 * own comment). Every tab reads on `storefront.view`; `branding`'s own
 * `status` field additionally needs `storefront.publish`, checked at the
 * call site rather than the route because it depends on the request body,
 * not the URL — `BrandingTab` gates the publish toggle itself.
 */
export const STOREFRONT_TABS: readonly RoutedTab<StorefrontTab>[] = [
  { id: 'branding', labelKey: 'storefront.tabs.branding', perms: ['storefront.view'] },
  { id: 'domains', labelKey: 'storefront.tabs.domains', perms: ['storefront.manage_domains'] },
  { id: 'branches', labelKey: 'storefront.tabs.branches', perms: ['storefront.view'] },
  { id: 'carts', labelKey: 'storefront.tabs.carts', perms: ['storefront.view'] },
];

export type MessagingTab = 'templates' | 'campaigns' | 'settings';

/**
 * `/messaging/<id>`. ADR-0026 §9 — templates, campaigns and the tenant-wide
 * send controls (quiet hours, the weekly fatigue cap). Consent itself is
 * ADR-0010's, surfaced on the CRM side (`ConsentTab`,
 * `SegmentMembersDialog`) rather than duplicated here.
 *
 * `message_templates.*` and `campaigns.*` are, by design, mapped to NO
 * system role except Tenant Owner/Admin — not even Customer Service, which
 * already holds `all('segments')` and defines the very audiences a campaign
 * targets (see `roles.ts` §9). Building a segment is choosing who COULD be
 * reached; activating a campaign is reaching them, and that split is
 * deliberate. `templates`/`campaigns` read on their own `.view` key;
 * `settings` reads on `campaigns.view` too — `MessagingSettingsController`
 * gates its own `GET` that way rather than a `message_settings.*` family.
 */
export const MESSAGING_TABS: readonly RoutedTab<MessagingTab>[] = [
  { id: 'templates', labelKey: 'messaging.tabs.templates', perms: ['message_templates.view'] },
  { id: 'campaigns', labelKey: 'messaging.tabs.campaigns', perms: ['campaigns.view'] },
  { id: 'settings', labelKey: 'messaging.tabs.settings', perms: ['campaigns.view'] },
];

export type DeliveryTab = 'board' | 'shifts' | 'drivers' | 'zones' | 'tracking' | 'analytics';

/**
 * `/delivery/<id>`. ADR-0029, §16 own delivery & driver operations.
 *
 * `board` is `deliveries.view` — the same key `DeliveriesController` demands
 * on every read, held by a dispatcher (who then sees everyone) and a DRIVER
 * (who then sees only their own assignment, filtered server-side). `shifts`
 * is `driver_shifts.view` for the identical reason. `drivers` and `zones` are
 * dispatch-tier config, gated on their own `.view` keys so a role holding one
 * but not the other still lands somewhere real rather than a 403 — the same
 * I-07 shape Fleet/Integrations/CentralKitchen already document. `tracking`
 * is deliberately its OWN key, `delivery_location.configure`, separate from
 * `delivery_zones.view` — a role that prices zones was never automatically
 * meant to switch on continuous location capture of a person.
 */
export const DELIVERY_TABS: readonly RoutedTab<DeliveryTab>[] = [
  { id: 'board', labelKey: 'delivery.tabs.board', perms: ['deliveries.view'] },
  { id: 'shifts', labelKey: 'delivery.tabs.shifts', perms: ['driver_shifts.view'] },
  { id: 'drivers', labelKey: 'delivery.tabs.drivers', perms: ['drivers.view'] },
  { id: 'zones', labelKey: 'delivery.tabs.zones', perms: ['delivery_zones.view'] },
  { id: 'tracking', labelKey: 'delivery.tabs.tracking', perms: ['delivery_location.configure'] },
  { id: 'analytics', labelKey: 'delivery.tabs.analytics', perms: ['deliveries.view'] },
];

export type BrandDetailTab = 'overview' | 'standards' | 'analytics' | 'royalty' | 'onboarding';

/**
 * `/brands/:brandId/<id>` — ADR-0023 §19. `overview` and `standards` ride the
 * route's own `brands.view` gate (a franchisee branch manager reads both,
 * `roles.ts`'s `BRANCH_MANAGER` comment); `analytics`, `royalty` and
 * `onboarding` are each their own permission family — deliberately narrower
 * than `brands.view`, because seeing a sibling franchisee's consolidated
 * revenue, its royalty figures, or being able to provision a new branch under
 * this brand are each a different authority from reading the brand's own
 * standards document.
 */
export const BRAND_DETAIL_TABS: readonly RoutedTab<BrandDetailTab>[] = [
  { id: 'overview', labelKey: 'brands.tabs.overview' },
  { id: 'standards', labelKey: 'brands.tabs.standards' },
  { id: 'analytics', labelKey: 'brands.tabs.analytics', perms: ['brands.analytics'] },
  { id: 'royalty', labelKey: 'brands.tabs.royalty', perms: ['royalty.view'] },
  { id: 'onboarding', labelKey: 'brands.tabs.onboarding', perms: ['brands.onboard'] },
];
